<script setup lang="ts">
import { computed } from 'vue';
import { AppCard, AppTypography } from '~/components/base';
import { useVotingAreaCounts } from '~/composables/useVotingAreaCounts';
import { useLocationStore } from '~/stores/location';

const emit = defineEmits<{ 'set-location': [] }>();

const store = useLocationStore();
const { lowerHouseCount, upperHouseCount } = useVotingAreaCounts(
  () => store.location?.suburbId ?? null,
);

const lowerHouseLabel = computed(() =>
  store.isSet && lowerHouseCount.value !== null
    ? `Browse (${lowerHouseCount.value}) →`
    : 'Browse All →',
);
const upperHouseLabel = computed(() =>
  store.isSet && upperHouseCount.value !== null
    ? `Browse (${upperHouseCount.value}) →`
    : 'Browse All →',
);

// Once a suburb is picked the tile titles become the user's actual district
// electorate and Upper House region instead of the chamber names.
const districtName = computed(() => store.location?.electorates[0] ?? null);
const regionName = computed(() => store.location?.region ?? null);
</script>

<template>
  <AppCard class="voting-area-card">
    <div class="voting-area-card__header mb-4 lg:mb-10">
      <div class="flex flex-col gap-1">
        <AppTypography type="title-6" class="text-color-brand">
          Your Voting Area
        </AppTypography>
        <AppTypography type="subheadline" class="voting-area-card__subtitle">
          Based on your location
        </AppTypography>
      </div>
      <button
        v-if="store.isSet"
        type="button"
        data-testid="voting-area-location-pill"
        class="voting-area-card__pill flex w-fit shrink-0 items-center gap-1"
        @click="emit('set-location')"
      >
        <span class="voting-area-card__icon-frame shrink-0">
          <Icon name="solar:map-point-linear" />
        </span>
        {{ store.displayLabel }}
      </button>
      <button
        v-else
        type="button"
        data-testid="set-location"
        class="voting-area-card__chip flex w-fit max-w-full shrink-0 items-center gap-1"
        aria-label="Set location to view your electorate"
        @click="emit('set-location')"
      >
        <span class="voting-area-card__icon-frame shrink-0">
          <Icon name="solar:map-point-linear" />
        </span>
        <span class="voting-area-card__chip-label"
          >Set location to view your electorate</span
        >
      </button>
    </div>

    <div class="grid grid-cols-2 gap-2">
      <NuxtLink
        to="/candidates"
        class="voting-area-card__tile voting-area-card__tile--district no-underline"
      >
        <div class="flex flex-col gap-1">
          <AppTypography type="footnote-bold" class="text-white uppercase">
            District<br class="lg:hidden" />
            Candidates
          </AppTypography>
          <AppTypography
            type="title-6"
            class="voting-area-card__chamber text-white"
            data-testid="district-tile-title"
          >
            <template v-if="districtName">{{ districtName }}</template>
            <template v-else>Lower House</template>
          </AppTypography>
        </div>
        <AppTypography
          type="footnote-bold"
          class="mt-auto pt-6 text-white underline"
        >
          {{ lowerHouseLabel }}
        </AppTypography>
      </NuxtLink>
      <NuxtLink
        to="/candidates?chamber=upper"
        class="voting-area-card__tile voting-area-card__tile--region no-underline"
      >
        <div class="flex flex-col gap-1">
          <AppTypography type="footnote-bold" class="text-white uppercase">
            Region<br class="lg:hidden" />
            Candidates
          </AppTypography>
          <AppTypography
            type="title-6"
            class="voting-area-card__chamber text-white"
            data-testid="region-tile-title"
          >
            <template v-if="regionName">{{ regionName }}</template>
            <template v-else>Upper House</template>
          </AppTypography>
        </div>
        <AppTypography
          type="footnote-bold"
          class="mt-auto pt-6 text-white underline"
        >
          {{ upperHouseLabel }}
        </AppTypography>
      </NuxtLink>
    </div>
  </AppCard>
</template>

<style scoped lang="scss">
.voting-area-card {
  // Mobile: Figma card body padding is 1em; desktop keeps PrimeVue's
  // default Card body padding (1.25rem), which was already correct.
  :deep(.p-card-body) {
    padding: 1em;

    @media (min-width: 1024px) {
      padding: 1.25rem;
    }
  }
}

// Mobile: title/subtitle stack above the location pill so the "Your Voting
// Area" heading gets the full card width and stays on one line (Figma).
// Desktop has room for the pill inline on the right.
.voting-area-card__header {
  display: flex;
  flex-direction: column;
  gap: $spacing-lg; // gap-4

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: $spacing-3xl; // gap-10
  }
}

.voting-area-card__subtitle {
  // Figma: text/primary (#201e1a) — this app's `.text-color-primary` token
  // is a different (navy) palette used elsewhere, so set the literal value.
  color: #201e1a;
}

// Selected-location chip (Figma "Chips"): tag fill inside a warm outline, at
// the same 64px radius as the unset CTA it replaces.
.voting-area-card__pill {
  // Figma: 40px overall. Without a set height the 1px border sits outside the
  // 24px content + 16px padding and the chip renders 42px.
  height: 40px;
  padding: $spacing-sm $spacing-md $spacing-sm $spacing-sm;
  border: 1px solid #e8e3db; // Figma: borders/default (warm)
  border-radius: $radius-6xl;
  background-color: $surface-color-tag;
  // Figma text/primary is the warm near-black, not $text-color-primary (navy).
  color: #201e1a;
  font-family: inherit;
  // Figma: Subheadline/Regular • 14
  font-size: 14px;
  line-height: 16px;
  cursor: pointer;
}

.voting-area-card__chip {
  @include font-subheadline;
  padding: $spacing-sm $spacing-md;
  border-radius: $radius-6xl;
  // Filled rather than the previous cream outline, matching the navbar's
  // "Choose Your Location" button so the two read as the same action.
  border: 1px solid $complementary-color-darker-shade-1;
  background-color: $complementary-color-darker-shade-1;
  color: $text-color-inverted;
  cursor: pointer;
}

.voting-area-card__icon-frame {
  @include icon-frame-sm;
}

.voting-area-card__chip-label {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.voting-area-card__tile {
  // Figma: 180px tall tiles with the Browse link pinned to the bottom, over an
  // aerial-photo texture behind the dark green.
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 180px;
  padding: $spacing-lg;
  border-radius: $radius-lg;
  // Stays as the fallback beneath the photo (and while it loads) so the white
  // text always has a dark surface under it.
  background-color: $surface-color-brand;
  background-repeat: no-repeat;
  background-size: cover;
  color: #fff;

  // Each tile has its own photo: streetscape for District, aerial for Region.
  // The exported images already carry the brand tint, so no extra gradient
  // overlay is layered on top — that would double-darken them.
  //
  // WebP (q92, visually transparent at this render size), not the original
  // PNGs: these two tiles ARE the mobile LCP element, and as CSS backgrounds
  // they're invisible to the preload scanner, so every byte sits directly on
  // the LCP critical path. 50KB+44KB of PNG became 4KB+3KB.
  //
  // Referenced from app/assets (not public/) on purpose: at this size Vite
  // INLINES them into this component's CSS as data URIs (assetsInlineLimit,
  // 4KB), so the pixels arrive inside the render-blocking stylesheet itself —
  // zero extra requests, zero discovery delay, nothing left to preload or
  // cache-header. If these images ever grow past the inline limit they fall
  // back to hashed /_nuxt/ files (immutable cache header, nginx disk serving,
  // CDN behavior), which is still strictly better than the old un-cacheable
  // public/ path — but consider re-adding a fetchpriority=high preload on the
  // home page in that case, since discovery delay returns with the request.
  &--district {
    background-image: url('~/assets/images/left-bg.webp');
    background-position: center;
  }

  &--region {
    background-image: url('~/assets/images/right-bg.webp');
    // Figma anchors the aerial shot to the bottom of the tile.
    background-position: center bottom;
  }
}

.voting-area-card__chamber {
  // Tile title ("Lower House" / district name) is Title 3 at regular weight, unlike
  // the bold Title 3 used elsewhere for this type — override just the weight.
  font-weight: 400;
}
</style>
